import { createStore } from './store';

export type ToastKind = 'pending' | 'success' | 'error' | 'info';

export type Toast = {
  id: string;
  kind: ToastKind;
  title: string;
  message?: string;
  txHash?: string;       // links to Etherscan
  createdAt: number;
};

type ToastState = { toasts: Toast[] };

export const toastStore = createStore<ToastState>({ toasts: [] });

let _id = 0;
function nextId(): string {
  _id = (_id + 1) | 0;
  return `t${_id}-${Date.now()}`;
}

/** Push a new toast onto the stack. Returns the id so callers can update it. */
export function pushToast(kind: ToastKind, title: string, opts: { message?: string; txHash?: string; autoDismissMs?: number } = {}): string {
  const id = nextId();
  const toast: Toast = {
    id,
    kind,
    title,
    message: opts.message,
    txHash: opts.txHash,
    createdAt: Date.now(),
  };
  const current = toastStore.get();
  toastStore.set({ toasts: [...current.toasts, toast] });
  // Auto-dismiss success/info/error after a delay; pending stays until updated.
  const autoMs =
    opts.autoDismissMs ??
    (kind === 'pending' ? null : kind === 'error' ? 8000 : 5000);
  if (autoMs !== null) {
    setTimeout(() => dismissToast(id), autoMs);
  }
  return id;
}

/** Mutate an existing toast in place (e.g. pending → success). */
export function updateToast(id: string, patch: Partial<Omit<Toast, 'id' | 'createdAt'>>): void {
  const current = toastStore.get();
  const next = current.toasts.map((t) => (t.id === id ? { ...t, ...patch } : t));
  toastStore.set({ toasts: next });
  // Re-arm auto-dismiss after a status update.
  const updated = next.find((t) => t.id === id);
  if (updated && updated.kind !== 'pending') {
    const autoMs = updated.kind === 'error' ? 8000 : 5000;
    setTimeout(() => dismissToast(id), autoMs);
  }
}

export function dismissToast(id: string): void {
  const current = toastStore.get();
  toastStore.set({ toasts: current.toasts.filter((t) => t.id !== id) });
}
